import { Component, OnInit, EventEmitter, Output, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { EventosService } from 'src/app/services/eventos.service';
import { CreateEventService } from 'src/app/services/create-event.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { SessionsService } from 'src/app/services/sessions.service';
import { formatDate } from '@angular/common';
import { forkJoin } from 'rxjs';
import { CreateSessionComponent } from '../../dialogs-event/create-session/create-session.component';
import { DaySessionsComponent } from '../../dialogs-event/day-sessions/day-sessions.component';
import { EditSessionComponent } from '../../dialogs-event/edit-session/edit-session.component';
import { InfoCardComponent } from 'src/app/components/shared/info-card/info-card.component';

interface CalendarDay {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  sessions: any[];
  events: Map<number, any>;
}

@Component({
  selector: 'app-sessions-event',
  templateUrl: './sessions-event.component.html',
  styleUrls: ['./sessions-event.component.scss']
})
export class SessionsEventComponent implements OnInit {
  @Input() idEvent: number;
  @Output() changeTab: EventEmitter<any> = new EventEmitter<any>();


  business = JSON.parse(localStorage.getItem('business') || '{}');
  currentDate: Date = new Date();
  currentMonth: number;
  currentYear: number;
  monthNames: string[] = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                          'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  weekDays: string[] = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

  calendarDays: CalendarDay[] = [];
  companyId: number;
  sessionsData: any = {};
  loading: boolean = false;
  eventsCache: Map<number, any> = new Map();
  viewMode: 'lista' | 'calendario' = 'lista';

  get listSessions(): any[] {
    const flat: any[] = [];
    Object.keys(this.sessionsData).forEach(dateKey => {
      (this.sessionsData[dateKey] as any[]).forEach((session: any) => {
        // Solo sesiones del evento actual o sesiones temporales (recién creadas)
        if (session._isTemporary || (this.idEvent && session.id_event === this.idEvent)) {
          flat.push(session);
        }
      });
    });
    flat.sort((a, b) => {
      const aTime = a.start_date ? new Date(a.start_date.replace(' ', 'T')).getTime() : 0;
      const bTime = b.start_date ? new Date(b.start_date.replace(' ', 'T')).getTime() : 0;
      return aTime - bTime;
    });
    return flat;
  }

  getListSessionMonth(session: any): string {
    const d = this.getSessionDate(session);
    if (!d) return '';
    return `${this.monthNames[d.getMonth()]} ${d.getFullYear()}`;
  }

  // Desde la lista se edita una sesión concreta, así que se abre directamente
  // su editor en vez de pasar por el diálogo del día (que obligaba a un
  // segundo clic sobre la única sesión que mostraba).
  openSessionFromList(session: any): void {
    const sessionData = this.findSessionData(session);
    if (!sessionData) {
      return;
    }
    this.openEditSessionDialog(sessionData);
  }

  // El par {session, event} que espera el editor lo construye createEventsMap,
  // que es también lo que consume el diálogo del día.
  private findSessionData(session: any): { session: any; event: any } | null {
    const dateKey = this.getSessionDateKey(session?.start_date);
    const sessionsForDay = this.sessionsData[dateKey] || [];
    const pairs = Array.from(this.createEventsMap(sessionsForDay).values());

    if (session._isTemporary) {
      return pairs.find((pair: any) => pair.session._isTemporary && pair.session.start_date === session.start_date) || null;
    }

    return pairs.find((pair: any) => pair.session.id === session.id) || null;
  }

  private openEditSessionDialog(sessionData: { session: any; event: any }): void {
    const dialogRef = this.dialog.open(EditSessionComponent, {
      panelClass: ['info-dialog', 'not-padding'],
      disableClose: true,
      width: '750px',
      data: {
        eventName: sessionData.event.title,
        sessionId: sessionData.session.id,
        idEvent: sessionData.session.id_event || sessionData.event.id,
        sessionData: sessionData.session,
        temporarySessionIndex: sessionData.session._temporaryIndex,
        existingSessions: []
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success) {
        this.updateCalendarLocally();
      } else if (result && !result.success && result.error) {
        this.dialog.open(InfoCardComponent, {
          panelClass: 'info-dialog',
          data: {
            icon: true,
            text: result.error,
            btnCancel: false
          }
        });
      }
    });
  }

  constructor(
    private eventosService: EventosService,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private createEventService: CreateEventService,
    private authService: AuthenticationService,
    private sessionsService: SessionsService
  ) {
    this.currentMonth = this.currentDate.getMonth();
    this.currentYear = this.currentDate.getFullYear();

    this.companyId = this.business.id;

    if (!this.companyId) {
      const profile = this.authService.getProfile();
      this.companyId = profile?.id_company || profile?.company?.id;
    }

    if (!this.companyId) {
      console.warn('No se pudo obtener companyId, usando valor por defecto');
      this.companyId = 2;
    }
  }

  ngOnInit(): void {
    // Obtener el idEvent de los parámetros de la ruta si no viene por @Input
    if (!this.idEvent) {
      this.activatedRoute.params.subscribe({
        next: (params) => {
          if (params.idEvent) {
            this.idEvent = +params.idEvent;
          }
        }
      });
    }

    this.loadSessions();
  }

  loadSessions(): void {
    this.loading = true;

    // Calcular los 3 meses: anterior, actual y siguiente
    const prevMonth = this.currentMonth === 0 ? 11 : this.currentMonth - 1;
    const prevYear = this.currentMonth === 0 ? this.currentYear - 1 : this.currentYear;
    const nextMonth = this.currentMonth === 11 ? 0 : this.currentMonth + 1;
    const nextYear = this.currentMonth === 11 ? this.currentYear + 1 : this.currentYear;

    const prevMonthParam = `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}`;
    const currentMonthParam = `${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}`;
    const nextMonthParam = `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}`;

    // Cargar sesiones de los 3 meses en paralelo
    forkJoin({
      prev: this.eventosService.getSessionsByMonth(this.companyId, prevMonthParam),
      current: this.eventosService.getSessionsByMonth(this.companyId, currentMonthParam),
      next: this.eventosService.getSessionsByMonth(this.companyId, nextMonthParam)
    }).subscribe({
      next: (responses: any) => {
        // Combinar todas las sesiones de los 3 meses
        this.sessionsData = {};

        if (responses.prev?.success && responses.prev?.data) {
          Object.assign(this.sessionsData, responses.prev.data);
        }
        if (responses.current?.success && responses.current?.data) {
          Object.assign(this.sessionsData, responses.current.data);
        }
        if (responses.next?.success && responses.next?.data) {
          Object.assign(this.sessionsData, responses.next.data);
        }

        // Agregar sesiones temporales del observable
        this.mergeTemporarySessions();
        // Agregar las sesiones ya guardadas de ESTE evento, aunque caigan fuera
        // de la ventana de 3 meses cargada arriba (p.ej. al editar un evento
        // pasado o muy futuro).
        this.mergeExistingEventSessions();

        this.loadEventDetails();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading sessions:', error);

        this.sessionsData = {};
        this.mergeTemporarySessions();
        this.mergeExistingEventSessions();

        this.generateCalendar();

        this.loading = false;
      }
    });
  }

  // El backend agrupa las sesiones por la parte de fecha (en UTC) de
  // start_date. Derivar la clave con formatDate la pasaría a la zona local
  // (p. ej. 22:00Z en horario de verano cae al día siguiente), lo que
  // desalinea la clave respecto a la del backend y provoca que la misma
  // sesión se añada a dos días distintos (se ve duplicada). Tomamos la parte
  // de fecha del string, igual que hacen getEventTime (getUTC*) y
  // openSessionFromList.
  private getSessionDateKey(startDate: any): string {
    if (!startDate) return '';
    if (typeof startDate === 'string') {
      return startDate.split('T')[0].split(' ')[0];
    }
    return formatDate(startDate, 'yyyy-MM-dd', 'en-US', 'UTC');
  }

  private mergeTemporarySessions(): void {
    const sessionsFromObservable = this.sessionsService.getSessionsSaved();
    if (sessionsFromObservable && sessionsFromObservable.length > 0) {
      sessionsFromObservable.forEach(session => {
        const dateKey = this.getSessionDateKey(session.start_date);
        if (!this.sessionsData[dateKey]) {
          this.sessionsData[dateKey] = [];
        }
        const tempSession = { ...session, _isTemporary: true };
        this.sessionsData[dateKey].push(tempSession);
      });
    }
  }

  // Las sesiones ya guardadas del evento que se está editando (cargadas junto con
  // el resto de la info del evento en CreateEventService) deben verse siempre,
  // independientemente del mes que muestre el calendario en ese momento.
  private mergeExistingEventSessions(): void {
    if (!this.idEvent) return;
    const savedSessions = this.createEventService.getInfoEvent()?.values?.sessions || [];
    savedSessions.forEach((session: any) => {
      if (!session?.start_date) return;
      const dateKey = this.getSessionDateKey(session.start_date);
      if (!this.sessionsData[dateKey]) {
        this.sessionsData[dateKey] = [];
      }
      const alreadyPresent = this.sessionsData[dateKey].some((s: any) => s.id && s.id === session.id);
      if (!alreadyPresent) {
        this.sessionsData[dateKey].push(session);
      }
    });
  }

  loadEventDetails(): void {
    const uniqueEventIds = new Set<number>();

    // Solo agregar eventos que NO están en caché
    Object.keys(this.sessionsData).forEach(dateKey => {
      this.sessionsData[dateKey].forEach((session: any) => {
        if (!session._isTemporary && session.id_event && !this.eventsCache.has(session.id_event)) {
          uniqueEventIds.add(session.id_event);
        }
      });
    });

    // Si no hay eventos por cargar, generar calendario directamente
    if (uniqueEventIds.size === 0) {
      this.generateCalendar();
      return;
    }else{
      // llenar this.eventsCache con los eventos que ya tenemos en las sesiones para no hacer llamadas innecesarias
      Object.keys(this.sessionsData).forEach(dateKey => {
        this.sessionsData[dateKey].forEach((session: any) => {
          if (!session._isTemporary && session.id_event && !this.eventsCache.has(session.id_event)) {
            this.eventsCache.set(session.id_event, session.event);
          }
        });
      });
      this.generateCalendar();
    }


    /*const eventRequests = Array.from(uniqueEventIds).map(eventId =>
      this.eventosService.getEventById(eventId)
    );

    forkJoin(eventRequests).subscribe({
      next: (responses: any[]) => {
        responses.forEach(response => {
          if (response.success && response.data) {
            this.eventsCache.set(response.data.id, response.data);
          }
        });
        this.generateCalendar();
      },
      error: (error) => {
        console.error('Error loading event details:', error);
        this.generateCalendar();
      }
    });*/
  }

  generateCalendar(): void {
    this.calendarDays = [];

    const firstDay = new Date(this.currentYear, this.currentMonth, 1);
    const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);

    // Ajustar para que la semana empiece en lunes (0 = domingo, 1 = lunes)
    let firstDayOfWeek = firstDay.getDay();
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    // Días del mes anterior
    const prevMonthLastDay = new Date(this.currentYear, this.currentMonth, 0);
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(this.currentYear, this.currentMonth - 1, prevMonthLastDay.getDate() - i);
      const dateKey = formatDate(date, 'yyyy-MM-dd', 'en-US');
      const sessions = this.sessionsData[dateKey] || [];

      // Crear mapa de eventos para días del mes anterior
      const eventsMap = this.createEventsMap(sessions);

      this.calendarDays.push({
        date: date,
        dayNumber: date.getDate(),
        isCurrentMonth: false,
        sessions: sessions,
        events: eventsMap
      });
    }

    // Días del mes actual
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(this.currentYear, this.currentMonth, day);
      const dateKey = formatDate(date, 'yyyy-MM-dd', 'en-US');
      const sessions = this.sessionsData[dateKey] || [];

      // Crear mapa de eventos con sus detalles
      const eventsMap = this.createEventsMap(sessions);

      this.calendarDays.push({
        date: date,
        dayNumber: day,
        isCurrentMonth: true,
        sessions: sessions,
        events: eventsMap
      });
    }

    // Días del próximo mes para completar la última semana
    const remainingDays = 42 - this.calendarDays.length; // 6 semanas * 7 días
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(this.currentYear, this.currentMonth + 1, day);
      const dateKey = formatDate(date, 'yyyy-MM-dd', 'en-US');
      const sessions = this.sessionsData[dateKey] || [];

      // Crear mapa de eventos para días del mes siguiente
      const eventsMap = this.createEventsMap(sessions);

      this.calendarDays.push({
        date: date,
        dayNumber: day,
        isCurrentMonth: false,
        sessions: sessions,
        events: eventsMap
      });
    }
  }

  private createEventsMap(sessions: any[]): Map<number, any> {
    const eventsMap = new Map<number, any>();

    sessions.forEach((session: any, index: number) => {
      // Si es una sesión temporal (marcada con _isTemporary)
      if (session._isTemporary) {
        const eventInfo = this.createEventService.getInfoEvent();
        const tempEvent = {
          id: null,
          title: eventInfo?.values?.title || 'Evento sin título',
          draft: true
        };

        // Encontrar el índice en el observable para poder editarla
        const observableSessions = this.sessionsService.getSessionsSaved();
        const temporaryIndex = observableSessions.findIndex(s =>
          s.start_date === session.start_date && s._isTemporary === true
        );

        eventsMap.set(index, {
          session: { ...session, _temporaryIndex: temporaryIndex },
          event: tempEvent
        });
      } else {
        // Para sesiones reales, usar los datos del cache
        let eventDetail = this.eventsCache.get(session.id_event);

        // Las sesiones del evento que se está editando deben ser siempre
        // editables, aunque su ficha todavía no esté en la caché (p.ej. por
        // timing al cargar el calendario). Sin este fallback, canEditSession()
        // en day-sessions no encuentra el evento y bloquea la edición.
        if (!eventDetail && this.idEvent && session.id_event === this.idEvent) {
          const eventInfo = this.createEventService.getInfoEvent();
          eventDetail = {
            id: this.idEvent,
            title: eventInfo?.values?.title || 'Evento sin título'
          };
        }

        if (eventDetail) {
          eventsMap.set(session.id, {
            session: session,
            event: eventDetail
          });
        }
      }
    });

    return eventsMap;
  }

  previousMonth(): void {
    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
    this.loadSessions();
  }

  nextMonth(): void {
    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
    this.loadSessions();
  }

  // Hora "de pared" de la sesión, como Date local y sin conversión de zona horaria.
  //
  // Conviven dos formatos y hay que tratarlos distinto:
  //  - Las sesiones ya guardadas llegan de la API como "2026-09-13T20:00:00.000Z":
  //    el backend etiqueta como UTC una hora que en realidad es local, así que las
  //    20:00 se recuperan leyendo los dígitos tal cual (equivale a los getters UTC).
  //  - Las sesiones temporales del alta (_isTemporary) son "2026-09-13 20:00:00" o
  //    un Date local, y ya están en hora local.
  //
  // Pintar las dos con date:...:'+0000' desplazaba las segundas: una sesión creada a
  // las 20:00 se listaba a las 18:00 en horario de verano.
  getSessionDate(session: any): Date | null {
    const value = session?.start_date;
    if (!value) return null;

    if (typeof value === 'string') {
      const parts = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
      if (parts) {
        const [, year, month, day, hours, minutes] = parts;
        return new Date(+year, +month - 1, +day, +hours, +minutes);
      }
    }

    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  getEventTime(session: any): string {
    const date = this.getSessionDate(session);
    if (!date) return '';

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${hours}:${minutes}`;
  }

  getVisibleSessions(day: CalendarDay): any[] {
    // Mostrar primero las sesiones más recientes (últimas agregadas)
    const sessions = Array.from(day.events.values()).reverse();
    return sessions.slice(0, 2);
  }

  getAdditionalSessionsCount(day: CalendarDay): number {
    const total = day.events.size;
    return total > 2 ? total - 2 : 0;
  }

  handleDayClick(day: CalendarDay, event: Event): void {
    if (this.isPastDay(day.date)) {
      this.dialog.open(InfoCardComponent, {
        panelClass: ['info-dialog'],
        disableClose: false,
        data: {
          icon: true,
          text: 'No puedes crear sesiones en días pasados',
          btnCancel: false
        }
      });
      return;
    }

    if (day.events.size > 0) {
      this.openDayDialog(day, event);
    } else {
      this.createSessionForDay(day, event);
    }
  }

  createSessionForDay(day: CalendarDay, event: Event): void {
    event.stopPropagation();

    if (this.isPastDay(day.date)) {
      this.dialog.open(InfoCardComponent, {
        panelClass: ['info-dialog'],
        disableClose: false,
        data: {
          icon: true,
          text: 'No puedes crear sesiones en días pasados',
          btnCancel: false
        }
      });
      return;
    }

    const eventInfo = this.createEventService.getInfoEvent();
    const eventTitle = eventInfo?.values?.title;

    if (!eventTitle || eventTitle.trim() === '') {
      this.dialog.open(InfoCardComponent, {
        panelClass: ['info-dialog'],
        disableClose: false,
        data: {
          icon: true,
          text: 'Debes agregar un título al evento antes de crear sesiones.',
          btnCancel: false
        }
      });
      return;
    }

    const selectedDate = new Date(
      day.date.getFullYear(),
      day.date.getMonth(),
      day.date.getDate(),
      0, 0, 0, 0
    );

    const dialogRef = this.dialog.open(CreateSessionComponent, {
      panelClass: ['info-dialog', 'not-padding'],
      disableClose: true,
      width: '750px',
      data: {
        eventName: eventTitle,
        idEvent: this.idEvent || null,
        isEditMode: !!this.idEvent,
        preselectedDate: selectedDate
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success) {
        this.updateCalendarLocally();
      }
    });
  }

  createSession(): void {
    const eventInfo = this.createEventService.getInfoEvent();
    const eventTitle = eventInfo?.values?.title;

    if (!eventTitle || eventTitle.trim() === '') {
      this.dialog.open(InfoCardComponent, {
        panelClass: ['info-dialog'],
        disableClose: false,
        data: {
          icon: true,
          text: 'Debes agregar un título al evento antes de crear sesiones.',
          btnCancel: false
        }
      });
      return;
    }

    const dialogRef = this.dialog.open(CreateSessionComponent, {
      panelClass: ['info-dialog', 'not-padding'],
      disableClose: true,
      width: '750px',
      data: {
        eventName: eventTitle,
        idEvent: this.idEvent || null,
        isEditMode: !!this.idEvent
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success) {
        this.updateCalendarLocally();
      }
    });
  }

  updateCalendarLocally(): void {
    if (this.idEvent) {
      this.loadSessions();
      return;
    }

    Object.keys(this.sessionsData).forEach(dateKey => {
      this.sessionsData[dateKey] = this.sessionsData[dateKey].filter((s: any) => !s._isTemporary);
      if (this.sessionsData[dateKey].length === 0) {
        delete this.sessionsData[dateKey];
      }
    });

    const sessionsFromObservable = this.sessionsService.getSessionsSaved();

    if (sessionsFromObservable && sessionsFromObservable.length > 0) {
      sessionsFromObservable.forEach(session => {
        const dateKey = this.getSessionDateKey(session.start_date);
        if (!this.sessionsData[dateKey]) {
          this.sessionsData[dateKey] = [];
        }
        const tempSession = { ...session, _isTemporary: true };
        this.sessionsData[dateKey].push(tempSession);
      });
    }

    this.generateCalendar();
  }

  nextStep(): void {
    // Guardar las sesiones en el CreateEventService antes de cambiar de paso
    const sessionsFromObservable = this.sessionsService.getSessionsSaved();

    if (sessionsFromObservable && sessionsFromObservable.length > 0) {
      const currentEventInfo = this.createEventService.getInfoEvent() || { values: {} };
      currentEventInfo.values.sessions = sessionsFromObservable;
      this.createEventService.setInfoDetail(currentEventInfo);
    }

    let step = { tab: 3, name: 'Entradas', slug: 'ticket-event' };
    this.changeTab.emit(step);
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }

  isPastDay(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  }

  getEventColorClass(sessionData: any): string {
    const session = sessionData.session;
    const event = sessionData.event;

    // Si es una sesión temporal (del observable) -> fucsia fuerte
    if (session._isTemporary) {
      return 'event-current';
    }

    // Si es una sesión del evento actual que estamos editando -> fucsia fuerte
    if (this.idEvent && session.id_event === this.idEvent) {
      return 'event-current';
    }

    // Si el evento está cancelado o es de prueba, usar clase cancelled
    if (event?.cancelled || event?.status === 'cancelled') {
      return 'event-cancelled';
    }

    // Si el evento es borrador, usar clase draft
    if (event?.draft === true) {
      return 'event-draft';
    }

    // Por defecto, evento publicado
    return 'event-published';
  }

  openDayDialog(day: CalendarDay, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (day.events.size === 0) {
      return;
    }

    const sessions = Array.from(day.events.values());

    const dialogRef = this.dialog.open(DaySessionsComponent, {
      panelClass: ['info-dialog', 'not-padding'],
      disableClose: false,
      width: '680px',
      data: {
        date: day.date,
        sessions: sessions,
        currentIdEvent: this.idEvent || null
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.updateCalendarLocally();
      }
    });
  }
}
